import {SbkModal} from "@/components/modal/modal";

declare global {
    interface Window {
        cookieconsent_options: {
            optIn: string
            overlay: {
                dataPrivacy: string
                image: string
                btn: string
                text: string
            }
            pid: string
        };
    }
}

type Cookie = {
    required: boolean
    statistics: boolean
    thirdParty: boolean
    cookieGroup?: boolean
}

//------------------------------------*\
// #Interface
//------------------------------------*/

interface ICookieLayer {
    cookieName: string
    cookieLayerWasInitialized: boolean
    editedSettings: boolean
    mmsCookies: boolean | string | Cookie | null | undefined
    initializeCookieLayer: () => void
    initializeCookieSettings: () => void
    initializeTrapFocus: () => void
    handleCookieSettingsBtn: () => void,
    setCookie: (statisticsCookies?: boolean, thirdPartyCookies?: boolean) => void
    isCookieSet: () => boolean
    setThirdPartyConsent: () => void
    getCookie: (cookieName: string) => string | null
    asyncLoad: (element: HTMLElement, callback?: (_: null, event: Event) => void) => void
    asyncJS: (element: HTMLElement, callback?: (_: null, event: Event) => void) => void
    asyncCSS: (element: HTMLElement) => void
    getCookieElementsByTag: (tag: string, selector?: string) => HTMLElement[]
    loadTags: () => void
    loadCookiesPreset: () => void
    callIframeHandler: (element: HTMLIFrameElement) => void
    callScriptHandler: (element: HTMLScriptElement) => void // this function is async!
    acceptThirdParty: () => void
    mobileCheck: () => boolean
    closeModal: (modalId: string) => void
    showModal: (modalId: string) => void
    trackCookieSettings: (statisticsCookies: boolean, showedInitial: boolean) => void
    initialize: () => void
    isCookieConsentLoaded: () => boolean

    cssClasses: {
        [key: string]: string
    }
    ids: {
        [key: string]: string
    }
    dataSet: {
        [key: string]: string
    }
}

//------------------------------------*\
// #Component
//------------------------------------*/

const CookieLayer: ICookieLayer = {
    cssClasses: {
        switch: 'toggle-switch',
        toggleSettingsOverlay: 'toggle-overlay--cookie-settings',
        hidden: 'hidden',
        cookieConsentOverlay: 'cookie-consent-overlay',
        cookieConsentLoaded: 'cookie-consent-loaded',
        googleMaps: 'googleMaps',
        overlay: 'overlay',
        video: 'responsive-video',
        cookieContent: 'modal__content',
        thirdPartyConcent: 'thirdPartyCookies-consent'
    },

    ids: {
        cookieLayer: 'mmsCookieLayer',
        cookieLayerSettings: 'mmsCookieLayerSettings',
        cookieLayerSettingsButton: 'mmsCookieLayerSettingsButton',
        statisticsCookies: 'statisticsCookies',
        thirdPartyCookies: 'thirdPartyCookies',
        acceptThirdPartyButton: 'acceptThirdPartyBtn',
        declineCookies: 'declineCookies',
        acceptButton: 'mmsCookieLayerAcceptButton',
        saveButton: 'mmsCookieLayerSaveButton',
        saveSettings: 'saveCookieSettings',
        closeButton: 'closeCookieLayer',
        cookieConsentOverlay: 'cookie-consent-overlay',
        googleMap: 'map',
        editedCookieSettings: 'editedCookieSettings',
        googleMapsScript: '__googleMapsScriptId'
    },

    dataSet: {
        google: 'data-google',
    },

    /**
     * Cookie name
     */
    cookieName: 'cookie-optIn',

    /**
     * Is cookie layer initialized
     */
    cookieLayerWasInitialized: false,

    /**
     * the cookies
     */
    mmsCookies: false,

    /**
     * edited the user the cookie settings afterwards
     */
    editedSettings: false,

    initialize: () => {
        CookieLayer.loadCookiesPreset();

        const cookieLayer: HTMLDivElement | null = document.querySelector(`#${CookieLayer.ids.cookieLayer}`)
        const cookieLayerSettings: HTMLDivElement | null = document.querySelector(`#${CookieLayer.ids.cookieLayerSettings}`)
        const editedCookieSettingsBtns: NodeListOf<HTMLButtonElement> = document.querySelectorAll(`#${CookieLayer.ids.editedCookieSettings}`)

        if (cookieLayer && !CookieLayer.isCookieSet() && !CookieLayer.mobileCheck()) {
            CookieLayer.initializeCookieLayer()
            CookieLayer.initializeTrapFocus()
        }

        if (CookieLayer.isCookieSet() || !cookieLayerSettings) {
            CookieLayer.loadTags()
        }

        if (cookieLayerSettings) {
            CookieLayer.initializeCookieSettings()

            cookieLayerSettings.addEventListener('modal-before-close', () => {

                if (!CookieLayer.editedSettings) {
                    CookieLayer.showModal(CookieLayer.ids.cookieLayer)
                }

                CookieLayer.editedSettings = false
            })
        }

        document.addEventListener('third-party-cookies-accepted', (event) => {
           if (event instanceof CustomEvent && !event.detail?.alreadySet) {
               CookieLayer.acceptThirdParty()
           }
        })

        if (editedCookieSettingsBtns !== null) {
            editedCookieSettingsBtns.forEach(editedCookieSettingsBtn => {
                editedCookieSettingsBtn.addEventListener('click', () => {
                    CookieLayer.handleCookieSettingsBtn()
                })
            });
        }
    },

    handleCookieSettingsBtn: () => {
        CookieLayer.editedSettings = true;
        CookieLayer.showModal(CookieLayer.ids.cookieLayerSettings);
    },

    initializeCookieLayer: () => {

        const declineCookies: HTMLAnchorElement | null = document.querySelector(`#${CookieLayer.ids.declineCookies}`)
        const settingsLinks: HTMLElement[] = Array.from(document.querySelectorAll(`.${CookieLayer.cssClasses.toggleSettingsOverlay}`))
        const cookieLayerContentDivs: HTMLDivElement[] = Array.from(document.querySelectorAll(`.${CookieLayer.cssClasses.cookieContent}`))
        const firstSettingsLink: HTMLAnchorElement | null = document.querySelector(`.${CookieLayer.cssClasses?.toggleSettingsOverlay}`)

        setTimeout(() => {
            CookieLayer.showModal(CookieLayer.ids.cookieLayer);
            if (firstSettingsLink)
                firstSettingsLink.addEventListener('focusout', () => {
                });
        }, 100);

        cookieLayerContentDivs.forEach(cookieLayerContentDiv => {
            setTimeout(() => {
                cookieLayerContentDiv.scrollTop = 0;
            }, 100);
        });

        CookieLayer.cookieLayerWasInitialized = true;
        CookieLayer.trackCookieSettings(false, true);

        declineCookies &&
        declineCookies.addEventListener('click', () => {
            CookieLayer.setCookie();
            CookieLayer.closeModal(CookieLayer.ids.cookieLayer);
        });

        if (settingsLinks.length) {
            settingsLinks.forEach(link => {
                link.addEventListener('click', () => {
                    CookieLayer.closeModal(CookieLayer.ids.cookieLayer);
                    CookieLayer.showModal(CookieLayer.ids.cookieLayerSettings);
                    link.focus(); // Set focus directly on the clicked link
                });
            });
        }
    },

    closeModal: (modalId) => {
        const modal: SbkModal | null = document.querySelector(`#${modalId}`)

        if (modal) {
            const dialog: HTMLDialogElement | null = modal.renderRoot.querySelector('dialog');
            dialog?.close()
        }
    },

    showModal: (modalId) => {
        const modal: SbkModal | null = document.querySelector(`#${modalId}`)

        if (modal) {
            const dialog: HTMLDialogElement | null = modal.renderRoot.querySelector('dialog');
            dialog?.showModal()
        }
    },

    initializeCookieSettings: () => {

        const acceptButton: HTMLButtonElement | null = document.querySelector(`#${CookieLayer.ids.acceptButton}`)
        const saveButton: HTMLButtonElement | null = document.querySelector(`#${CookieLayer.ids.saveButton}`)
        const saveCookieSettings: HTMLButtonElement | null = document.querySelector(`#${CookieLayer.ids.saveSettings}`)

        acceptButton &&
        acceptButton.addEventListener('click', () => {
            acceptButton.classList.add(CookieLayer.cssClasses.hidden)
            saveButton?.classList.remove(CookieLayer.cssClasses.hidden)
        })

        saveButton &&
        saveButton.addEventListener('click', () => {
            CookieLayer.setCookie(true, true)
            CookieLayer.closeModal(CookieLayer.ids.cookieLayer)
        })

        saveCookieSettings &&
        saveCookieSettings.addEventListener('click', () => {
            const statisticsCookies: HTMLInputElement | null = document.querySelector(`#${CookieLayer.ids.statisticsCookies}`)
            const thirdPartyCookies: HTMLInputElement | null = document.querySelector(`#${CookieLayer.ids.thirdPartyCookies}`)
            let thirdPartyCookiesValue = false;
            let statisticsCookiesValue = false;
            if (thirdPartyCookies) {
                thirdPartyCookiesValue = thirdPartyCookies.checked
            }
            if (statisticsCookies) {
                statisticsCookiesValue = statisticsCookies.checked
            }
            CookieLayer.setCookie(statisticsCookiesValue, thirdPartyCookiesValue)
            CookieLayer.closeModal(CookieLayer.ids.cookieLayerSettings)
        })
    },

    initializeTrapFocus: () => {
        const firstSettingsLink: HTMLAnchorElement | null = document.querySelector(`.${CookieLayer.cssClasses.toggleSettingsOverlay}`)
        const acceptButton: HTMLButtonElement | null = document.querySelector(`#${CookieLayer.ids.acceptButton}`)

        acceptButton &&
        acceptButton.addEventListener('focusout', () => {
            firstSettingsLink?.focus();
            firstSettingsLink?.classList.add('keyboard-focus'); // Apply keyboard-focus class to the acceptButton
        });
    },

    setCookie: (statisticsCookies = false, thirdPartyCookies = false) => {
        const expDate: Date = new Date()
        expDate.setDate(expDate.getDate() + 30)

        const value: Cookie = {
            required: true,
            statistics: statisticsCookies,
            thirdParty: thirdPartyCookies,
        }

        // dont set secure for local environment
        if (document.URL.indexOf('mms-dresden.de') != -1) {
            document.cookie = `${CookieLayer.cookieName}=${JSON.stringify(value)};path=/;expires=${expDate.toUTCString()}` + ';SameSite=Strict;'
        } else {
            document.cookie = `${CookieLayer.cookieName}=${JSON.stringify(value)};path=/;expires=${expDate.toUTCString()}` + ';SameSite=None; Secure'
        }

        CookieLayer.mmsCookies = value

        const statisticsSwitch: HTMLInputElement | null = document.querySelector(`#${CookieLayer.ids.statisticsCookies}`);
        if (statisticsSwitch) {
            statisticsSwitch.checked = statisticsCookies;
        }
        const thirdPartySwitch: HTMLInputElement | null = document.querySelector(`#${CookieLayer.ids.thirdPartyCookies}`);
        if (thirdPartySwitch) {
            if (thirdPartyCookies) {
                const cookiesAcceptedEvent = new CustomEvent('third-party-cookies-accepted', {
                    detail: {
                        alreadySet: true,
                    },
                    bubbles: true,
                    composed: true,
                })
                document.dispatchEvent(cookiesAcceptedEvent);
            }
            thirdPartySwitch.checked = thirdPartyCookies;
        }

        if (CookieLayer.isCookieSet()) {
            CookieLayer.loadTags()
            CookieLayer.trackCookieSettings(statisticsCookies, false)
        }
    },

    isCookieSet: () => {
        const cookie: string | null = CookieLayer.getCookie(CookieLayer.cookieName)

        return cookie !== null
    },

    setThirdPartyConsent: () => {
        const videoContainers: HTMLDivElement[] = Array.from(document.querySelectorAll(`.${CookieLayer.cssClasses?.video}`))

        videoContainers?.forEach(videoContainer => {
            const cookieConsents: HTMLElement[] = Array.from(videoContainer.querySelectorAll(`.${CookieLayer.cssClasses?.cookieConsentLoaded}`))

            cookieConsents?.forEach(cookieConsent => {
                cookieConsent?.parentElement?.classList.add(CookieLayer.cssClasses.thirdPartyConcent)
            });
        });
    },

    getCookie: (cookieName) => {
        const name = `${cookieName}=`
        const decodedCookies: string = decodeURIComponent(document.cookie)
        const cookies: string[] = decodedCookies.split(';')

        const trimmedCookies: string[] = cookies.map((cookie: string) => cookie.trim())

        const cookieWithCookieName: string | undefined = trimmedCookies.find((cookie) => cookie.indexOf(name) === 0)

        if (cookieWithCookieName) {
            const cookieKeyValue: string[] = cookieWithCookieName.split('=')
            return cookieKeyValue[1]
        } else {
            return null
        }
    },

    // Async load resources (script- and link-tags)
    asyncLoad: (element: HTMLElement, callback?: (_: null, event: Event) => void) => {
        if (element instanceof HTMLScriptElement) {
            element.src = element.dataset.src ?? '';
            element.defer = true;
            element.async = true;
        } else if (element instanceof HTMLLinkElement) {
            element.rel = 'stylesheet';
            element.type = 'text/css';
            element.href = element.dataset.src ?? '';
            element.setAttribute('defer', '');
        }

        if (callback) {
            element.addEventListener('load', (event: Event) => callback(null, event), false)
        }
    },

    // Async load helper for JS
    asyncJS: (element: HTMLElement, callback?: (_: null, event: Event) => void) => {
        CookieLayer.asyncLoad(element, callback)
    },

    // Function not used
    // Async load helper for CSS
    asyncCSS: (element: HTMLElement) => {
        CookieLayer.asyncLoad(element)
    },

    getCookieElementsByTag: (tag: string, selector?: string) => {
        const attributeName = typeof selector === 'undefined' ? 'data-cookieconsent' : selector

        return Array.from(document.querySelectorAll(`${tag}[${attributeName}]`))
    },

    loadTags: () => {
        // Get all scripts and iframes to load
        const elements = [...CookieLayer.getCookieElementsByTag('script'), ...CookieLayer.getCookieElementsByTag('iframe')]
        elements.length > 0 &&
        elements.forEach((element: HTMLElement) => {
            if (window.cookieconsent_options !== undefined && window.cookieconsent_options.optIn === '1') {
                const cookieGroup: string | undefined = element.dataset.cookieconsent
                if (cookieGroup && cookieGroup !== 'required') {
                    if (!CookieLayer.mmsCookies) return

                    if (
                        /* eslint-disable no-prototype-builtins */
                        !(CookieLayer.mmsCookies as Cookie).hasOwnProperty(cookieGroup) ||
                        // @ts-expect-error No index signature with a parameter of type string was found on type Cookie
                        (CookieLayer.mmsCookies as Cookie)[cookieGroup] !== true
                        /* eslint-enable no-prototype-builtins */
                    ) {
                        return
                    }
                }
            }

            if (typeof element.tagName !== 'undefined') {
                switch (element.tagName.toUpperCase()) {
                    case 'IFRAME': {
                        CookieLayer.callIframeHandler(element as HTMLIFrameElement)
                        break
                    }
                    default: {
                        CookieLayer.callScriptHandler(element as HTMLScriptElement)
                    }
                }
            }
        })
    },

    loadCookiesPreset: () => {
        CookieLayer.mmsCookies = CookieLayer.getCookie(CookieLayer.cookieName)

        if (CookieLayer.mmsCookies) {
            try {
                CookieLayer.mmsCookies = JSON.parse(CookieLayer.mmsCookies)
            } catch {
                CookieLayer.mmsCookies = false
            }
        } else {
            CookieLayer.mmsCookies = false
        }
    },

    callIframeHandler: (element) => {
        const iframe = element.cloneNode(true) as HTMLIFrameElement

        // Set src to data-src
        if (iframe.dataset.src) {
            iframe.src = iframe.dataset.src
        }

        // Replace old iframe with new one and add it to DOM
        element?.parentNode?.replaceChild(iframe, element)

        // Add "loaded" css class
        iframe.classList.add(CookieLayer.cssClasses.cookieConsentLoaded)
        CookieLayer.setThirdPartyConsent()

        // Override attribute to only load once
        iframe.setAttribute('data-cookieconsent-loaded', iframe.getAttribute('data-cookieconsent') || '')
        iframe.removeAttribute('data-cookieconsent')

        const overlay = document.querySelector(`#${CookieLayer.ids.cookieConsentOverlay}`)
        overlay !== null && overlay.remove()
    },

    callScriptHandler: async (element) => {

        // Run js code if script tag contains executable code
        const jsCode = element.innerHTML

        if (jsCode && jsCode.length) {
            // Trim code
            const trimmedCode = await jsCode.trim()
            // Execute code
            eval.call(CookieLayer, trimmedCode)
        } else {
            CookieLayer.asyncJS(element)
        }

        // Override attribute to only load once
        element.setAttribute('data-cookieconsent-loaded', element.getAttribute('data-cookieconsent') || '')
        element.removeAttribute('data-cookieconsent')
    },


    acceptThirdParty: () => {
        CookieLayer.setCookie((CookieLayer.mmsCookies as Cookie).statistics ?? false, true)
    },

    mobileCheck: () => {
        return navigator.userAgent.match('OGSMobileAppRequest') ? true : false
    },

    trackCookieSettings: (statisticsCookies: boolean, showedInitial: boolean) => {
        const pid = window.cookieconsent_options.pid
        const edited = CookieLayer.editedSettings ? 1 : 0
        const xmlhttp = new XMLHttpRequest()
        const statistics = statisticsCookies ? 1 : 0
        const initial = showedInitial ? 1 : 0
        xmlhttp.open("GET", `?eID=trackCookieSettings&statistics=${statistics}&edited=${edited}&pid=${pid}&showedInitial=${initial}`, true)
        xmlhttp.send()
    },

    isCookieConsentLoaded: () => {
        const cookieString = CookieLayer.getCookie(CookieLayer.cookieName)
        if (cookieString) {
            const cookie: Cookie = JSON.parse(cookieString)
            return cookie.thirdParty || false;
        }
        return false
    }
}

export default CookieLayer
